import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, FileText, MessageSquare, Users, 
  ArrowRight, Layers, ArrowUpRight, Image,
  Calendar, Star, Eye, Clock
} from 'lucide-react';
import { dashboardService } from '../../services/api';

function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadStats();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardService.getStats();
      console.log('Stats reçues:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary-200 rounded-full animate-spin border-t-primary-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-primary-600 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Services', 
      value: stats?.services || 0, 
      icon: Briefcase, 
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      link: '/admin/services'
    },
    { 
      label: 'Applications', 
      value: stats?.applications || 0, 
      icon: Layers, 
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      link: '/admin/applications'
    },
    { 
      label: 'Articles', 
      value: stats?.articles || 0, 
      icon: FileText, 
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      link: '/admin/articles'
    },
    { 
      label: 'Messages', 
      value: stats?.contacts || 0, 
      icon: MessageSquare, 
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      link: '/admin/contacts', 
      badge: stats?.unreadContacts
    },
    { 
      label: 'Témoignages', 
      value: stats?.testimonials || 0, 
      icon: Star, 
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      iconColor: 'text-pink-600',
      link: '/admin/testimonials'
    },
    { 
      label: 'Médias', 
      value: stats?.medias || 0, 
      icon: Image, 
      color: 'from-cyan-500 to-teal-500',
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      link: '/admin/medias'
    },
    { 
      label: 'Utilisateurs', 
      value: stats?.users || 0, 
      icon: Users, 
      color: 'from-indigo-500 to-violet-500',
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      link: '/admin/users'
    },
  ];

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-8"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        {statCards.map((stat, index) => (
          <motion.div key={stat.label} variants={item}>
            <Link to={stat.link} className="block group">
              <div className="relative bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-dark-100">
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`}></div>
                
                <div className="relative">
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                  </div>
                  
                  <p className="text-3xl font-bold text-dark-900 mb-1">{stat.value}</p>
                  <p className="text-dark-500 text-sm font-medium">{stat.label}</p>
                  
                  {stat.badge > 0 && (
                    <span className="absolute top-0 right-0 px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold animate-pulse">
                      {stat.badge}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <motion.div variants={item} className="lg:col-span-2">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-dark-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-dark-900">Visites cette semaine</h3>
                <p className="text-dark-500 text-sm">Statistiques de trafic</p>
              </div>
            </div>
            
            {/* Chart */}
            <div className="h-64 flex items-end justify-between gap-3">
              {(stats?.visitsThisWeek || []).map((day, i) => {
                const maxVisits = Math.max(...(stats?.visitsThisWeek || []).map(d => d.visits), 1);
                const heightPercent = (day.visits / maxVisits) * 100;
                
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${heightPercent}%` }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className="w-full bg-gradient-to-t from-primary-600 to-primary-400 rounded-t-lg relative group cursor-pointer min-h-[20px]"
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-dark-900 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.visits} visites
                      </div>
                    </motion.div>
                    <span className="text-xs text-dark-400 mt-2">{day.day}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div variants={item}>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-dark-100 h-full">
            <h3 className="text-lg font-bold text-dark-900 mb-6">Résumé</h3>
            
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-dark-600">Services actifs</span>
                  <span className="font-bold text-dark-900">{stats?.services || 0}</span>
                </div>
                <div className="h-2 bg-dark-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((stats?.services || 0) * 10, 100)}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-dark-600">Applications</span>
                  <span className="font-bold text-dark-900">{stats?.applications || 0}</span>
                </div>
                <div className="h-2 bg-dark-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((stats?.applications || 0) * 10, 100)}%` }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-dark-600">Articles publiés</span>
                  <span className="font-bold text-dark-900">{stats?.articles || 0}</span>
                </div>
                <div className="h-2 bg-dark-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((stats?.articles || 0) * 10, 100)}%` }}
                    transition={{ duration: 1, delay: 0.7 }}
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-dark-600">Messages reçus</span>
                  <span className="font-bold text-dark-900">{stats?.contacts || 0}</span>
                </div>
                <div className="h-2 bg-dark-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((stats?.contacts || 0) * 5, 100)}%` }}
                    transition={{ duration: 1, delay: 0.8 }}
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-dark-100">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-dark-900">{stats?.medias || 0}</p>
                  <p className="text-xs text-dark-500">Médias</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-dark-900">{stats?.testimonials || 0}</p>
                  <p className="text-xs text-dark-500">Avis</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-dark-900">{stats?.users || 0}</p>
                  <p className="text-xs text-dark-500">Users</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Actions rapides */}
        <motion.div variants={item}>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-dark-100">
            <h3 className="text-lg font-bold text-dark-900 mb-4">Actions rapides</h3>
            <div className="space-y-2">
              {[
                { label: 'Nouveau service', path: '/admin/services', color: 'bg-blue-500', icon: Briefcase },
                { label: 'Nouvelle application', path: '/admin/applications', color: 'bg-purple-500', icon: Layers },
                { label: 'Écrire un article', path: '/admin/articles', color: 'bg-emerald-500', icon: FileText },
                { label: 'Voir les messages', path: '/admin/contacts', color: 'bg-amber-500', icon: MessageSquare },
              ].map((action, i) => (
                <Link
                  key={i}
                  to={action.path}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-dark-50 transition-colors group"
                >
                  <div className={`w-8 h-8 ${action.color} rounded-lg flex items-center justify-center`}>
                    <action.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-dark-700 group-hover:text-dark-900 font-medium">{action.label}</span>
                  <ArrowRight className="w-4 h-4 text-dark-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Derniers messages */}
        <motion.div variants={item}>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-dark-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-dark-900">Derniers messages</h3>
              <Link to="/admin/contacts" className="text-primary-600 text-sm hover:underline">Voir tout</Link>
            </div>
            
            {stats?.recentContacts?.length > 0 ? (
              <div className="space-y-3">
                {stats.recentContacts.slice(0, 4).map((contact, i) => (
                  <div key={contact.id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-dark-50 transition-colors">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center text-white font-bold">
                      {contact.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-dark-900 truncate">{contact.name}</p>
                      <p className="text-sm text-dark-500 truncate">{contact.subject || contact.email}</p>
                    </div>
                    {contact.status === 'unread' && (
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-dark-200 mx-auto mb-2" />
                <p className="text-dark-500">Aucun message</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Date et heure */}
        <motion.div variants={item}>
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white h-full">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-6 h-6" />
              <h3 className="text-lg font-bold">Aujourd'hui</h3>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-6xl font-bold mb-2">{currentTime.getDate()}</p>
              <p className="text-primary-200 text-lg">
                {currentTime.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </p>
              <p className="text-primary-200">
                {currentTime.toLocaleDateString('fr-FR', { weekday: 'long' })}
              </p>
            </div>

            <div className="bg-white/10 rounded-xl p-4 backdrop-blur text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-sm">Heure actuelle</span>
              </div>
              <p className="text-3xl font-mono font-bold">
                {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>

            {/* Dernières applications */}
            {stats?.recentApplications?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm text-primary-200 mb-2">Dernière application</p>
                <p className="font-medium truncate">{stats.recentApplications[0]?.title}</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default DashboardPage;